import React from 'react'
import Backdrop from '@mui/material/Backdrop';
import { Box, useTheme, Typography } from "@mui/material";
import { tokens } from "../../../../theme";
import Modal from '@mui/material/Modal';
import Fade from '@mui/material/Fade';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import { FileUploadOutlined } from '@mui/icons-material';
import ClearIcon from '@mui/icons-material/Clear';
import Axios from 'axios';
import { useDropzone } from 'react-dropzone';
import { getAuthHeader } from '../../../../utils/authToken';

const FeedbackModal = ({ 
    submission,
    setSuccessAlertOpen,
    setSuccessAlertMessage,
    setErrorAlertOpen,
    setErrorAlertMessage,
    setBackdropOpen,
}) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const style = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 400,
        bgcolor: colors.primary[600],
        border: '2px solid #000',
        boxShadow: 24,
        p: 4,
    };

    const [author, setAuthor] = React.useState("");
    const [files, setFiles] = React.useState([]);
    const [open, setOpen] = React.useState(false);
    const handleOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);

    const onDrop = React.useCallback((acceptedFiles) => {
        setFiles(prevFiles => [...prevFiles, ...acceptedFiles]);
    }, []);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        multiple: true
    });

    const handleFileRemove = (event, index) => {
        event.preventDefault();
        const newFiles = [...files];
        newFiles.splice(index, 1);
        setFiles(newFiles);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (author === "") {
            setErrorAlertOpen(true);
            setErrorAlertMessage("Please enter an author.");
            return;
        }

        if (files.length === 0 && event.target.comment.value === "") {
            setErrorAlertOpen(true);
            setErrorAlertMessage("Please enter a comment or upload at least one file.");
            return;
        }

        setBackdropOpen(true);
        const formData = new FormData();
        formData.append('comment', event.target.comment.value);
        formData.append('author', author);
        formData.append('hw_submission_id', submission.id);
        for (let i = 0; i < files.length; i++) {
            formData.append('file', files[i]);
        }

        await Axios.post(`${process.env.REACT_APP_URL}/api/v1/courses/${window.location.pathname.split('/')[2]}/homeworks/${window.location.pathname.split('/')[4]}/feedback`, 
            formData, {
                headers: {
                    Authorization: getAuthHeader(),
                    'Content-Type': 'multipart/form-data'
                }
        }).then((response) => {
            setSuccessAlertOpen(true);
            setSuccessAlertMessage(response.data.message);
            setBackdropOpen(false);
            handleClose();
            window.location.reload();
        }).catch((error) => {
            console.log(error.response?.data?.message);
            setErrorAlertOpen(true);
            setErrorAlertMessage(error.response?.data?.message || "Something went wrong");
            setBackdropOpen(false);
        });
    };

    return (
        <div>
            <Button
                type="submit"
                variant="contained"
                style={{
                    backgroundColor: colors.primary[300],
                    color: "white",
                    width: "8rem",
                    marginTop: "0.5rem",
                    fontSize: "0.6rem",
                }}
                size='small'
                onClick={handleOpen}
            >
                Upload Feedback
            </Button>
            <Modal
                aria-labelledby="transition-modal-title"
                aria-describedby="transition-modal-description"
                open={open}
                onClose={handleClose}
                closeAfterTransition
                slots={{ backdrop: Backdrop }}
                slotProps={{
                    backdrop: {
                        timeout: 500,
                    },
                }}
            >
                <Fade in={open}>
                    <Box sx={style}>
                        <Box
                            display="flex"
                            flexDirection="column"
                            gap={2}
                            component="form"
                            onSubmit={(e) => handleSubmit(e)}
                        >
                            <TextField
                                id="filled-basic"
                                label="Author"
                                variant="filled"
                                sx={{
                                    '& .MuiFilledInput-root': {
                                        backgroundColor: colors.primary[750],
                                        ":focus": {
                                            backgroundColor: 'white',
                                        },
                                    },
                                }}
                                onChange={(e) => setAuthor(e.target.value)}
                            />
                            <TextField
                                id="filled-basic"
                                label="Comment (optional)"
                                variant="filled"
                                name='comment'
                                fullWidth={true}
                                multiline={true}
                                rows={10}
                                sx={{
                                    minWidth: "100%",
                                    borderRadius: "5px",
                                    '& .MuiFilledInput-root': {
                                        ":focus": {
                                            backgroundColor: 'white',
                                        },
                                    },
                                }}
                            />
                            <Box
                                display="flex"
                                alignItems="center"
                                mb={-2}
                            >
                                <Typography variant="h6" fontWeight="bold" color={colors.primary[450]}>
                                    Upload Files
                                </Typography>
                            </Box>
                            
                            {/* React Dropzone Area */}
                            <Box
                                {...getRootProps()}
                                sx={{
                                    border: `2px dashed ${colors.primary[450]}`,
                                    borderRadius: '8px',
                                    padding: '20px',
                                    textAlign: 'center',
                                    cursor: 'pointer',
                                    backgroundColor: isDragActive ? colors.primary[700] : colors.primary[750],
                                    transition: 'background-color 0.2s ease',
                                    '&:hover': {
                                        backgroundColor: colors.primary[700],
                                    }
                                }}
                            >
                                <input {...getInputProps()} />
                                <FileUploadOutlined sx={{ fontSize: 40, color: colors.primary[450], mb: 1 }} />
                                <Typography variant="body1" color={colors.primary[450]}>
                                    {isDragActive 
                                        ? "Drop the files here..." 
                                        : "Drag & drop files here, or click to browse"
                                    }
                                </Typography>
                            </Box>

                            <Box
                                display="flex"
                                minWidth="100%"
                                flexDirection="column"
                            >
                                {files.length > 0 && files.map((file, index) => {
                                    return (
                                        <Box key={index} display="flex" alignItems="center">
                                            <Typography variant="body1" color={colors.primary[450]}>
                                                {file.name}
                                            </Typography>
                                            <IconButton onClick={(e) => handleFileRemove(e, index)}>
                                                <ClearIcon />
                                            </IconButton>
                                        </Box>
                                    )
                                })}
                            </Box>
                            <Button
                                type="submit"
                                variant="contained"
                                style={{
                                    backgroundColor: colors.primary[300],
                                    color: "white",
                                    marginTop: "0.5rem",
                                    width: "10rem",
                                }}
                            >
                                Upload
                            </Button>
                        </Box>
                    </Box>
                </Fade>
            </Modal>
        </div>
    );
}

export default FeedbackModal